class HyrikTabs extends HTMLElement {
  constructor() {
    super();
    this.breakpoint = 750;
    this.mq = null;
    this._raf = 0;
  }

  connectedCallback() {
    this.init();

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", (e) => this.reinitializeSection(e.detail.sectionId));
      document.addEventListener("shopify:block:select", (e) => this.activateBlock(e.detail.sectionId, e.detail.blockId));
    }
  }

  disconnectedCallback() {
    if (this.mq) this.mq.removeEventListener?.("change", this.onMqChange);
    window.removeEventListener("resize", this.onResize);
    cancelAnimationFrame(this._raf);
  }

  init() {
    this.tabList = this.querySelector(".hyrik_tabs");
    this.tabs = Array.from(this.querySelectorAll(".hyrik_tabs li"));
    this.contents = Array.from(this.querySelectorAll(".hyrik_tab-content"));
    this.accHeads = Array.from(this.querySelectorAll(".hyrik_acc-head"));

    if (!this.tabList || !this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        this.clearActiveTabs();
        this.addActiveTab(tab);
      });
    });

    this.accHeads.forEach((head) => {
      head.addEventListener("click", (e) => {
        const parentContent = e.currentTarget.closest(".hyrik_tab-content");
        if (!parentContent) return;

        if (parentContent.classList.contains("is-active")) {
          parentContent.classList.remove("is-active");
          return;
        }

        this.contents.forEach((c) => c.classList.remove("is-active"));
        parentContent.classList.add("is-active");

        // Sync active tab so switching back to tab mode keeps state
        if (parentContent.id) {
          const matchTab = this.querySelector(`.hyrik_tabs li a[href="#${CSS.escape(parentContent.id)}"]`)?.parentElement;
          if (matchTab) {
            this.tabs.forEach((t) => t.classList.remove("is-active"));
            matchTab.classList.add("is-active");
          }
        }
      });
    });

    this.mq = matchMedia(`(max-width: ${this.breakpoint - 1}px)`);
    this.onMqChange = () => this.scheduleLayoutCheck();
    this.onResize = () => this.scheduleLayoutCheck();

    this.mq.addEventListener?.("change", this.onMqChange);
    window.addEventListener("resize", this.onResize);

    if (!this.querySelector(".hyrik_tabs li.is-active")) this.addActiveTab(this.tabs[0]);
    this.handleResponsiveMode();
  }

  scheduleLayoutCheck() {
    cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this.handleResponsiveMode());
  }

  handleResponsiveMode() {
    if (!this.tabList) return;

    const below = this.mq ? this.mq.matches : window.innerWidth < this.breakpoint;
    const overflow = this.tabList.scrollWidth - this.tabList.clientWidth > 18;

    if (overflow || below) {
      this.classList.add("accmode");
      return;
    }

    this.classList.remove("accmode");

    // Keep content ↔ tab consistent when returning to tab mode
    const activeContent = this.querySelector(".hyrik_tab-content.is-active");
    if (!this.querySelector(".hyrik_tabs li.is-active") && activeContent?.id) {
      const matchTab = this.querySelector(`.hyrik_tabs li a[href="#${CSS.escape(activeContent.id)}"]`)?.parentElement;
      if (matchTab) {
        this.clearActiveTabs();
        this.addActiveTab(matchTab);
        return;
      }
    }

    if (!this.querySelector(".hyrik_tabs li.is-active")) this.addActiveTab(this.tabs[0]);
  }

  clearActiveTabs() {
    this.querySelectorAll(".hyrik_tabs li, .hyrik_tab-content").forEach((el) => el.classList.remove("is-active"));
  }

  addActiveTab(tab) {
    if (!tab) return;
    tab.classList.add("is-active");

    const href = tab.querySelector("a")?.getAttribute("href");
    if (!href) return;

    const matchingContent = this.querySelector(href);
    if (matchingContent) matchingContent.classList.add("is-active");
  }

  reinitializeSection(sectionId) {
    const sectionElement = document.querySelector(`#hyrik_section-${sectionId}`);
    if (!sectionElement) return;

    const el = sectionElement.querySelector("hyrik-tabs") || sectionElement;
    if (el?.init) el.init();
  }

  activateBlock(sectionId, blockId) {
    const sectionElement = document.querySelector(`#hyrik_section-${sectionId}`);
    if (!sectionElement) return;

    const el = sectionElement.querySelector("hyrik-tabs") || sectionElement;
    if (!el) return;

    const tabContent = el.querySelector(`.tab-${blockId}`);
    if (!tabContent?.id) return;

    const tab = el.querySelector(`.hyrik_tabs li a[href="#${CSS.escape(tabContent.id)}"]`)?.parentElement;
    if (!tab) return;

    el.clearActiveTabs();
    el.addActiveTab(tab);
    el.handleResponsiveMode();
  }
}

customElements.define("hyrik-tabs", HyrikTabs);
